import type { PlainMessage } from "@bufbuild/protobuf";
import { useCallback, useMemo, useState } from "react";
import type { UserBalanceWithFundSource } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import type { FundSourceFilterCounts } from "../../libs/fund-source-filters";
import { t } from "../../libs/i18n";
import {
  calculateBalances,
  distributeBalances,
  formatMismatchMessage,
} from "./utils/calculate-balances";

export type PayForm = {
  balances: FormBalance[];
  payAmount: number | undefined;
  totalAmount: number;
  valid: boolean;
  errorMessage: string | null;
  hasAvailableBalance: boolean;
  onChangePayAmount: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeBalanceAmount: (
    e: React.ChangeEvent<HTMLInputElement>,
    fundSourceId: string,
  ) => void;
};

type BalanceState = {
  amount: number;
  valid: boolean;
  errorMessage: string | null;
};

export type FormBalance = PlainMessage<UserBalanceWithFundSource> &
  FundSourceFilterCounts & {
    balanceState: BalanceState;
    fundSourceId: string;
    isAvailable?: boolean;
    unavailableReason?: string | null;
  };

type PayFormProps = {
  initialBalances: (UserBalanceWithFundSource & FundSourceFilterCounts)[];
  availabilityByFundSourceId?: Record<
    string,
    {
      isAvailable: boolean;
      unavailableReason: string | null;
    }
  >;
};

export default function usePayForm({
  initialBalances,
  availabilityByFundSourceId = {},
}: PayFormProps): PayForm {
  const [balances, setBalances] = useState<FormBalance[]>(() => {
    return initialBalances.map((balance) => ({
      ...balance,
      fundSourceId: balance.fundSource?.id ?? "", // ここが空になるケースはない
      isAvailable:
        availabilityByFundSourceId[balance.fundSource?.id ?? ""]?.isAvailable ??
        true,
      unavailableReason:
        availabilityByFundSourceId[balance.fundSource?.id ?? ""]
          ?.unavailableReason ?? null,
      balanceState: {
        amount: 0,
        valid: false,
        errorMessage: null,
      },
    }));
  });
  const [payAmount, setPayAmount] = useState<number | undefined>(undefined);
  const [valid, setValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const totalAmount = useMemo(() => {
    return balances.reduce(
      (acc, balance) => acc + Number(balance.userBalance?.amount) || 0,
      0,
    );
  }, [balances]);

  const hasAvailableBalance = useMemo(
    () => balances.some((balance) => balance.isAvailable),
    [balances],
  );

  const validatePayAmount = useCallback(
    (
      payAmount: number | undefined,
      balances: FormBalance[],
    ): [boolean, string | null] => {
      let valid = true;
      let errorMessage = null;

      if (payAmount === undefined) {
        return [false, t("ポイントを入力してください")];
      }

      const totalAmount = balances.reduce(
        (acc, balance) => acc + Number(balance.balanceState.amount),
        0,
      );

      if (payAmount !== totalAmount) {
        valid = false;
        errorMessage = formatMismatchMessage(totalAmount, payAmount);
      }

      return [valid, errorMessage];
    },
    [],
  );

  const validateNumber = useCallback((n: number): [boolean, string | null] => {
    if (!Number.isInteger(n)) {
      return [false, t("整数を入力してください")];
    }

    if (n <= 0) {
      return [false, t("1以上のポイントを入力してください")];
    }

    return [true, null];
  }, []);

  const onChangePayAmount = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value.trim();

      if (inputValue === "") {
        setPayAmount(0);
        setValid(false);
        setErrorMessage(null);

        // 各バランスの金額をリセット
        setBalances((prev) =>
          prev.map((balance) => ({
            ...balance,
            balanceState: {
              amount: 0,
              valid: false,
              errorMessage: null,
            },
          })),
        );
        return;
      }

      const value = Number(inputValue);

      if (Number.isNaN(value)) {
        // 数値以外の入力を許可しない
        // undefined を入れると数値以外が入力されてしまうため 0 を入れる
        setPayAmount(0);
        return;
      }

      // 100万pt以上は上限超過としてエラーにし、残高分配は行わない
      if (value >= 1_000_000) {
        setPayAmount(value);
        setValid(false);
        setErrorMessage(t("一度に支払いできるポイントの上限を超えています"));
        return;
      }

      setPayAmount(value);

      // 入力値のバリデーション
      const [isValid, message] = validateNumber(value);
      if (!isValid) {
        setValid(isValid);
        setErrorMessage(message);
        return;
      }

      // 優先度に基づいてポイントを自動分配する
      const {
        updatedBalances,
        valid: resultValid,
        errorMessages: resultErrorMessages,
      } = distributeBalances({
        balances,
        payAmount: value,
        validate: validateNumber,
      });

      setBalances(updatedBalances);
      setValid(resultValid);
      setErrorMessage(
        resultErrorMessages.length > 0 ? resultErrorMessages[0] : null,
      );

      // ポイントの合計が一致しない場合、エラーメッセージを設定
      if (resultValid) {
        const [valid, errorMessage] = validatePayAmount(value, updatedBalances);
        if (!valid) {
          setValid(valid);
          setErrorMessage(errorMessage);
        }
      }
    },
    [validateNumber, balances, validatePayAmount],
  );

  const onChangeBalanceAmount = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, fundSourceId: string) => {
      const inputValue = e.target.value.trim();
      const value = inputValue === "" ? 0 : Number(inputValue);

      if (Number.isNaN(value)) {
        return; // 数値以外の入力は無視
      }

      const {
        updatedBalances,
        valid: isValid,
        errorMessages: resultErrorMessages,
        newPayAmount,
      } = calculateBalances({
        balances,
        fundSourceId,
        newAmount: value,
        payAmount,
      });

      setBalances(updatedBalances);
      setValid(isValid);
      setErrorMessage(resultErrorMessages[0]);

      let nextPayAmount = payAmount;

      // payAmountがない場合、newPayAmountを設定
      if (payAmount === undefined && newPayAmount !== undefined) {
        nextPayAmount = newPayAmount;
      } else {
        nextPayAmount = payAmount;
      }
      setPayAmount(nextPayAmount);

      // ポイントの合計が一致しない場合、エラーメッセージを設定
      if (isValid) {
        const [valid, errorMessage] = validatePayAmount(
          nextPayAmount,
          updatedBalances,
        );
        if (!valid) {
          setValid(valid);
          setErrorMessage(errorMessage);
        }
      }
    },
    [balances, payAmount, validatePayAmount],
  );

  return {
    balances,
    totalAmount,
    payAmount,
    valid,
    errorMessage,
    hasAvailableBalance,
    onChangePayAmount,
    onChangeBalanceAmount,
  };
}
